import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const SectionBlock = styled.section`
  margin-top: 1.5rem;
`;

const HeaderRow = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 0.9rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const ModalFormGrid = styled(FormGrid)`
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const InputWithAction = styled.div`
  display: flex;
  gap: 0.45rem;
  align-items: center;
`;

const Input = styled.input`
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 0.65rem;
  background: rgba(15, 23, 42, 0.04);
  color: ${({ theme }) => theme.colors.text};
  padding: 0.55rem 0.65rem;
`;

const Select = styled.select`
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 0.65rem;
  background: rgba(15, 23, 42, 0.04);
  color: ${({ theme }) => theme.colors.text};
  padding: 0.55rem 0.65rem;
`;

const PrimaryButton = styled.button`
  border: 0;
  border-radius: 0.65rem;
  padding: 0.58rem 0.86rem;
  background: #2563eb;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  width: fit-content;
`;

const SecondaryButton = styled.button`
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 0.65rem;
  padding: 0.58rem 0.86rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 700;
  cursor: pointer;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
  border-radius: 0.8rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 780px;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.7rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textMuted};
  border-bottom: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
  background: rgba(15, 23, 42, 0.03);
`;

const Td = styled.td`
  padding: 0.7rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
  vertical-align: middle;
  font-size: 0.93rem;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
`;

const ActionButton = styled.button`
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 0.55rem;
  padding: 0.4rem 0.58rem;
  font-size: 0.83rem;
  cursor: pointer;
  background: ${({ $danger }) => ($danger ? 'rgba(239, 68, 68, 0.08)' : 'transparent')};
  color: ${({ $danger }) => ($danger ? '#dc2626' : '#1d4ed8')};
`;

const EmptyRow = styled.p`
  margin: 0.9rem 0 0;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Message = styled.p`
  margin: 0.9rem 0 0;
  min-height: 1.2rem;
  color: ${({ error }) => (error ? '#fca5a5' : '#93c5fd')};
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.62);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 400;
  padding: 1rem;
`;

const ModalCard = styled.div`
  width: min(920px, 100%);
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1rem;
  box-shadow: 0 20px 40px rgba(2, 6, 23, 0.32);
  padding: 1.3rem;
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 1.05rem;
`;

const ModalActions = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
`;

const HelperText = styled.p`
  margin: 0.35rem 0 0;
  min-height: 1rem;
  font-size: 0.82rem;
  color: ${({ $error }) => ($error ? '#fca5a5' : '#86efac')};
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.roles) ? parsed : null;
  } catch {
    return null;
  }
};

const createEmptyTeacherForm = () => ({
  teacher_name: '',
  english_name: '',
  english_nickname: '',
  phone: '',
  email: '',
  teacher_login_id: '',
  hire_date: '',
  employment_status: '재직',
  role_type: '강사',
  specialization: '선택안함',
  employment_type: 'full_time',
});

const normalizeTeacher = (teacher = {}) => ({
  id: teacher.id,
  teacher_name: teacher.teacher_name || '',
  english_name: teacher.english_name || '',
  english_nickname: teacher.english_nickname || '',
  phone: teacher.phone || '',
  email: teacher.email || '',
  teacher_login_id: teacher.login_id || '',
  hire_date: teacher.hire_date || '',
  employment_status: teacher.employment_status === '퇴사' ? '퇴사' : '재직',
  role_type: teacher.role_type || '강사',
  specialization: teacher.specialization || '선택안함',
  employment_type: teacher.employment_type || 'full_time',
});

const PortalTeacherManagementPage = () => {
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const [teachers, setTeachers] = useState([]);
  const [newTeacher, setNewTeacher] = useState(createEmptyTeacherForm());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [detailTeacher, setDetailTeacher] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [isCheckingLoginId, setIsCheckingLoginId] = useState(false);
  const [isLoginIdChecked, setIsLoginIdChecked] = useState(false);
  const [loginIdCheckMessage, setLoginIdCheckMessage] = useState('');
  const [isLoginIdAvailable, setIsLoginIdAvailable] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchTeachers = async () => {
      if (!portalUser?.login_id) return;
      try {
        const data = await request({
          endpoint: `${BACKEND_BASE_URL}/api/admin/teachers?login_id=${encodeURIComponent(portalUser.login_id)}`,
        });
        const nextTeachers = Array.isArray(data?.teachers) ? data.teachers.map(normalizeTeacher) : [];
        setTeachers(nextTeachers);
        setMessage('');
      } catch (err) {
        setTeachers([]);
        setMessage(err?.responseData?.message || '선생님 목록을 불러오지 못했습니다.');
      }
    };

    fetchTeachers();
  }, [portalUser?.login_id, request]);

  const handleAddTeacher = async () => {
    if (!portalUser?.login_id) return;
    const teacherName = String(newTeacher?.teacher_name || '').trim();
    const teacherLoginId = String(newTeacher?.teacher_login_id || '').trim();
    if (!teacherName) {
      setMessage('선생님 이름을 입력해 주세요.');
      return;
    }
    if (!teacherLoginId) {
      setMessage('포털아이디를 입력해 주세요.');
      return;
    }
    if (!isLoginIdChecked || !isLoginIdAvailable) {
      setMessage('포털아이디 중복 체크를 먼저 완료해 주세요.');
      return;
    }
    try {
      setSavingId('new');
      setMessage('등록중입니다');
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/admin/teachers`,
        method: 'POST',
        body: {
          ...newTeacher,
          login_id: portalUser.login_id,
          teacher_name: teacherName,
          login_id_for_teacher: teacherLoginId,
        },
      });
      if (data?.teacher) {
        setTeachers((prev) => [normalizeTeacher(data.teacher), ...prev]);
      }
      setMessage('추가되었습니다');
      setNewTeacher(createEmptyTeacherForm());
      setIsLoginIdChecked(false);
      setIsLoginIdAvailable(false);
      setLoginIdCheckMessage('');
      await new Promise((resolve) => setTimeout(resolve, 450));
      setIsAddModalOpen(false);
    } catch (err) {
      if (err?.status === 409) {
        setMessage(err?.responseData?.message || '포털아이디가 이미 사용 중입니다.');
      } else {
        setMessage(err?.responseData?.message || '추가를 실패했습니다');
      }
    } finally {
      setSavingId(null);
    }
  };

  const handleUpdateTeacher = async (teacher) => {
    if (!portalUser?.login_id || !teacher?.id) return;
    if (!teacher.teacher_name.trim()) {
      setMessage('선생님 이름은 비워둘 수 없습니다.');
      return;
    }
    try {
      setSavingId(teacher.id);
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/admin/teachers/${teacher.id}`,
        method: 'PUT',
        body: {
          teacher_name: teacher.teacher_name,
          english_name: teacher.english_name,
          english_nickname: teacher.english_nickname,
          phone: teacher.phone,
          email: teacher.email,
          hire_date: teacher.hire_date,
          employment_status: teacher.employment_status,
          role_type: teacher.role_type,
          specialization: teacher.specialization,
          employment_type: teacher.employment_type,
          login_id: portalUser.login_id,
          login_id_for_teacher: teacher.teacher_login_id,
        },
      });
      if (data?.teacher) {
        const updated = normalizeTeacher(data.teacher);
        setTeachers((prev) => prev.map((item) => (item.id === teacher.id ? updated : item)));
        setDetailTeacher(null);
      }
      setMessage('선생님 정보가 수정되었습니다.');
    } catch (err) {
      setMessage(err?.responseData?.message || '선생님 수정에 실패했습니다.');
    } finally {
      setSavingId(null);
    }
  };

  const handleCheckTeacherLoginId = async () => {
    if (!portalUser?.login_id) return;
    const teacherLoginId = String(newTeacher?.teacher_login_id || '').trim();
    if (!teacherLoginId) {
      setLoginIdCheckMessage('포털아이디를 입력해 주세요.');
      setIsLoginIdChecked(false);
      setIsLoginIdAvailable(false);
      return;
    }
    try {
      setIsCheckingLoginId(true);
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/admin/teachers/check-login-id`,
        method: 'POST',
        body: {
          login_id: portalUser.login_id,
          teacher_login_id: teacherLoginId,
        },
      });
      const available = Boolean(data?.available);
      setIsLoginIdChecked(true);
      setIsLoginIdAvailable(available);
      setLoginIdCheckMessage(data?.message || (available ? '사용 가능한 포털아이디입니다.' : '이미 사용 중인 포털아이디입니다.'));
    } catch (err) {
      setIsLoginIdChecked(false);
      setIsLoginIdAvailable(false);
      setLoginIdCheckMessage(err?.responseData?.message || '중복 확인에 실패했습니다.');
    } finally {
      setIsCheckingLoginId(false);
    }
  };

  const handleRetireTeacher = async (teacherId) => {
    if (!portalUser?.login_id || !teacherId) return;
    try {
      setSavingId(teacherId);
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/admin/teachers/${teacherId}`,
        method: 'DELETE',
        body: { login_id: portalUser.login_id },
      });
      if (data?.teacher) {
        const updated = normalizeTeacher(data.teacher);
        setTeachers((prev) => prev.map((item) => (item.id === teacherId ? updated : item)));
      } else {
        setTeachers((prev) =>
          prev.map((item) => (item.id === teacherId ? { ...item, employment_status: '퇴사' } : item))
        );
      }
      setMessage('선생님 퇴사 처리가 완료되었습니다.');
    } catch (err) {
      setMessage(err?.responseData?.message || '퇴사 처리에 실패했습니다.');
    } finally {
      setSavingId(null);
    }
  };

  const updateDetailTeacherDraft = (key, value) => {
    setDetailTeacher((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  return (
    <Wrapper>
      <SectionHeading
        eyebrow="ADMIN"
        title="선생님 정보 관리"
        description="선생님 계정과 연락처 등 정보를 등록·수정하고, 퇴사 처리까지 여기서 할 수 있어요."
      />

      <HeaderRow>
        <PrimaryButton type="button" onClick={() => setIsAddModalOpen(true)}>
          선생님 추가
        </PrimaryButton>
      </HeaderRow>

      <SectionBlock>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>한글명</Th>
                <Th>연락처</Th>
                <Th>이메일</Th>
                <Th>과목</Th>
                <Th>재직 상태</Th>
                <Th>관리</Th>
              </tr>
            </thead>
            <tbody>
              {teachers.map((teacher) => (
                <tr key={teacher.id}>
                  <Td>{teacher.teacher_name || '-'}</Td>
                  <Td>{teacher.phone || '-'}</Td>
                  <Td>{teacher.email || '-'}</Td>
                  <Td>{teacher.specialization || '선택안함'}</Td>
                  <Td>{teacher.employment_status || '재직'}</Td>
                  <Td>
                    <RowActions>
                      <ActionButton
                        type="button"
                        onClick={() => setDetailTeacher({ ...teacher })}
                      >
                        상세정보
                      </ActionButton>
                      <ActionButton
                        type="button"
                        $danger
                        onClick={() => handleRetireTeacher(teacher.id)}
                        disabled={loading || savingId === teacher.id}
                      >
                        퇴사 처리
                      </ActionButton>
                    </RowActions>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
        {!loading && teachers.length === 0 && <EmptyRow>등록된 선생님이 없습니다.</EmptyRow>}
      </SectionBlock>
      <Message error={/실패|오류|못|권한/.test(message)}>{message}</Message>

      {isAddModalOpen && (
        <ModalBackdrop
          onClick={() => {
            setIsAddModalOpen(false);
            setIsLoginIdChecked(false);
            setIsLoginIdAvailable(false);
            setLoginIdCheckMessage('');
          }}
        >
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalTitle>선생님 추가</ModalTitle>
            <ModalFormGrid>
              <Label>
                한글명
                <Input
                  value={newTeacher.teacher_name}
                  onChange={(e) => setNewTeacher((prev) => ({ ...prev, teacher_name: e.target.value }))}
                />
              </Label>
              <Label>
                영문명
                <Input
                  value={newTeacher.english_name}
                  onChange={(e) => setNewTeacher((prev) => ({ ...prev, english_name: e.target.value }))}
                />
              </Label>
              <Label>
                영어닉네임
                <Input
                  value={newTeacher.english_nickname}
                  onChange={(e) => setNewTeacher((prev) => ({ ...prev, english_nickname: e.target.value }))}
                />
              </Label>
              <Label>
                전화번호
                <Input
                  value={newTeacher.phone}
                  onChange={(e) => setNewTeacher((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </Label>
              <Label>
                이메일
                <Input
                  value={newTeacher.email}
                  onChange={(e) => setNewTeacher((prev) => ({ ...prev, email: e.target.value }))}
                />
              </Label>
              <Label>
                포털아이디
                <InputWithAction>
                  <Input
                    value={newTeacher.teacher_login_id}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setNewTeacher((prev) => ({ ...prev, teacher_login_id: nextValue }));
                      setIsLoginIdChecked(false);
                      setIsLoginIdAvailable(false);
                      setLoginIdCheckMessage('');
                    }}
                  />
                  <SecondaryButton type="button" onClick={handleCheckTeacherLoginId} disabled={isCheckingLoginId || loading}>
                    {isCheckingLoginId ? '확인 중...' : '중복 체크'}
                  </SecondaryButton>
                </InputWithAction>
                <HelperText $error={isLoginIdChecked && !isLoginIdAvailable}>{loginIdCheckMessage}</HelperText>
              </Label>
              <Label>
                재직상태
                <Select
                  value={newTeacher.employment_status}
                  onChange={(e) => setNewTeacher((prev) => ({ ...prev, employment_status: e.target.value }))}
                >
                  <option value="재직">재직</option>
                  <option value="퇴사">퇴사</option>
                </Select>
              </Label>
              <Label>
                역할
                <Input value={newTeacher.role_type} onChange={(e) => setNewTeacher((prev) => ({ ...prev, role_type: e.target.value }))} />
              </Label>
              <Label>
                과목
                <Select
                  value={newTeacher.specialization}
                  onChange={(e) => setNewTeacher((prev) => ({ ...prev, specialization: e.target.value }))}
                >
                  <option value="선택안함">선택안함</option>
                  <option value="블록코딩">블록코딩</option>
                  <option value="파이썬">파이썬</option>
                  <option value="기타">기타</option>
                </Select>
              </Label>
              <Label>
                입사일
                <Input
                  type="date"
                  value={newTeacher.hire_date}
                  onChange={(e) => setNewTeacher((prev) => ({ ...prev, hire_date: e.target.value }))}
                />
              </Label>
              <Label>
                비고
                <Input
                  value={newTeacher.employment_type}
                  onChange={(e) => setNewTeacher((prev) => ({ ...prev, employment_type: e.target.value }))}
                />
              </Label>
            </ModalFormGrid>
            <ModalActions>
              <SecondaryButton
                type="button"
                onClick={() => {
                  setIsAddModalOpen(false);
                  setIsLoginIdChecked(false);
                  setIsLoginIdAvailable(false);
                  setLoginIdCheckMessage('');
                }}
              >
                취소
              </SecondaryButton>
              <PrimaryButton type="button" onClick={handleAddTeacher} disabled={loading || savingId === 'new'}>
                {savingId === 'new' ? '등록 중...' : '추가'}
              </PrimaryButton>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      )}

      {detailTeacher && (
        <ModalBackdrop onClick={() => setDetailTeacher(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalTitle>선생님 상세 정보 수정</ModalTitle>
            <ModalFormGrid>
              <Label>
                한글명
                <Input
                  value={detailTeacher.teacher_name}
                  onChange={(e) => updateDetailTeacherDraft('teacher_name', e.target.value)}
                />
              </Label>
              <Label>
                영문명
                <Input
                  value={detailTeacher.english_name}
                  onChange={(e) => updateDetailTeacherDraft('english_name', e.target.value)}
                />
              </Label>
              <Label>
                영어닉네임
                <Input
                  value={detailTeacher.english_nickname}
                  onChange={(e) => updateDetailTeacherDraft('english_nickname', e.target.value)}
                />
              </Label>
              <Label>
                전화번호
                <Input value={detailTeacher.phone} onChange={(e) => updateDetailTeacherDraft('phone', e.target.value)} />
              </Label>
              <Label>
                이메일
                <Input value={detailTeacher.email} onChange={(e) => updateDetailTeacherDraft('email', e.target.value)} />
              </Label>
              <Label>
                포털아이디
                <Input
                  value={detailTeacher.teacher_login_id}
                  readOnly
                />
              </Label>
              <Label>
                재직상태
                <Select
                  value={detailTeacher.employment_status}
                  onChange={(e) => updateDetailTeacherDraft('employment_status', e.target.value)}
                >
                  <option value="재직">재직</option>
                  <option value="퇴사">퇴사</option>
                </Select>
              </Label>
              <Label>
                역할
                <Input value={detailTeacher.role_type} onChange={(e) => updateDetailTeacherDraft('role_type', e.target.value)} />
              </Label>
              <Label>
                과목
                <Select
                  value={detailTeacher.specialization}
                  onChange={(e) => updateDetailTeacherDraft('specialization', e.target.value)}
                >
                  <option value="선택안함">선택안함</option>
                  <option value="블록코딩">블록코딩</option>
                  <option value="파이썬">파이썬</option>
                  <option value="기타">기타</option>
                </Select>
              </Label>
              <Label>
                입사일
                <Input
                  type="date"
                  value={detailTeacher.hire_date}
                  onChange={(e) => updateDetailTeacherDraft('hire_date', e.target.value)}
                />
              </Label>
              <Label>
                비고
                <Input
                  value={detailTeacher.employment_type}
                  onChange={(e) => updateDetailTeacherDraft('employment_type', e.target.value)}
                />
              </Label>
            </ModalFormGrid>
            <ModalActions>
              <SecondaryButton type="button" onClick={() => setDetailTeacher(null)}>
                닫기
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={() => handleUpdateTeacher(detailTeacher)}
                disabled={loading || savingId === detailTeacher.id}
              >
                {savingId === detailTeacher.id ? '저장 중...' : '저장'}
              </PrimaryButton>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      )}
    </Wrapper>
  );
};

export default PortalTeacherManagementPage;
